import React, { useEffect, FC } from "react";
import SingleChallengeGeneralInfo from "./SingleChallengeGeneralInfo";
import { singleChallengeProps } from "../../utils/singleChallengeProps";
import SingleChallengeAdditionalInfoWithTooltip from "./SingleChallengeAdditionalInfoWithTooltip";
import SingleChallengeFormula from "../SingleChallengeFormula/SingleChallengeFormula";
import { brand } from "branchAndBrandSpecific/utils/brandVariables/brandVariables";
import { partnerRoute } from "branchAndBrandSpecific/utils/brandVariables/siteRoutes/partnerRoutes";
import CreatorCard from "@/components/CreatorCard/CreatorCard";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import ChallengeTerms from "./ChallengeTerms/ChallengeTerms";
import ChallengeDisclaimer from "./ChallengeDisclaimer/ChallengeDisclaimer";
import { clientAtom } from "@/utils/atoms/atomClient";
import { useAtom } from "jotai";
import {
  Badge,
  Box,
  Flex,
  Grid,
  Separator,
  Skeleton,
  Tooltip,
} from "@radix-ui/themes";
import { CoverImage } from "@/components";
import ProgressBar from "@/components/ProgressBar/ProgressBar";
import { Zones } from "@/models/database/zones/Zones.model";
import useSingleZone from "@/features/zones/hooks/useSingleZone";

export const SingleChallengeUi: FC<{
  data: singleChallengeProps;
  isPreview: boolean;
  isPrivateForUser: boolean;
  additionalFields: {
    title: string;
    value: string;
    toolTipKey: string;
  }[];
  isEdit: boolean;
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
  children: React.ReactNode;
  zone: Zones;
}> = ({
  data,
  isPreview,
  isPrivateForUser,
  additionalFields,
  isEdit,
  setIsEdit,
  children,
  zone,
}): JSX.Element => {
  useEffect(() => {
    if (!data.isCreatedByCurrUser && setIsEdit !== undefined) {
      setIsEdit(false);
    }
  }, [isEdit]);
  // const setCurrentClient = useSetAtom(clientAtom)
  const [currentClient, setCurrentClient] = useAtom(clientAtom);

  // Page Document Title
  useDocumentTitle(`${data.challengeData.name} - Challenge`);

  // Variables
  const ChallengeCreator = data.creatorProfile;
  const ChallengeGame = data.challengeData.gameType;
  const isEventSite = brand.event.isEvent;
  const ChallengeClient = data.challengeData.client;
  const ChallengeType = data.challengeData.type;
  const isPrivateChallenge = ChallengeType === "private";
  const isRacing =
    data.challengeData.gameType.includes("fm8") ||
    data.challengeData.gameType.includes("lego-f1") ||
    data.challengeData.gameType.includes("f12024");
  const zoneId = data.challengeData?.zone;
  const { zoneItem, loading: loadingZone } = useSingleZone(zoneId);

  // Set different themes for individual partner pages
  useEffect(() => {
    if (brand.brandTheme === "elgiganten") {
      const partnerKeys = Object.keys(partnerRoute);

      partnerKeys.forEach((key) => {
        const partner = partnerRoute[key].partner;

        if (ChallengeClient === partner) {
          document.documentElement.className = partnerRoute[key].theme;
          setCurrentClient(partner);
        }
      });
    }
  }, [brand.brandTheme, partnerRoute, data]);

  const isFormulaOne = data.challengeData.gameType === "f12024";

  return (
    <Box>
      <CoverImage
        src={data.challengeData.logoUrl}
        alt={data.challengeData.name}
        heading={
          zone
            ? `${data.challengeData.name} by ${zone.title}`
            : data.challengeData.name
        }
        desc={data.challengeData.goal}
        badges={
          <Flex gap="3">
            {isPrivateChallenge && (
              <Tooltip content="This challenge is private, and only visible to you and other users that you have invited.">
                <Badge>{ChallengeType}</Badge>
              </Tooltip>
            )}
            <Tooltip
              content={`Game played in this challenge is ${data.challengeData.game}.`}
            >
              <Badge>
                {isFormulaOne ? "EA SPORTS™ F1® 24" : data.challengeData.game}
              </Badge>
            </Tooltip>
          </Flex>
        }
      >
        {/* Progress bar */}
        {!isPrivateForUser && (
          <ProgressBar
            startDate={
              data.challengeData.startDate as unknown as {
                seconds: number;
                nanoseconds: number;
              }
            }
            endDate={
              data.challengeData.endDate as unknown as {
                seconds: number;
                nanoseconds: number;
              }
            }
          />
        )}
      </CoverImage>

      <Grid gap="9" columns={{ initial: "1", md: "3" }}>
        {/*  Leaderboard */}
        <Box
          gridColumnStart={{ initial: "1", md: "1" }}
          gridColumnEnd={{ initial: "2", md: "3" }}
        >
          {children}
        </Box>

        {/* General info */}
        <Box
          gridColumnStart={{ initial: "1", md: "3" }}
          gridColumnEnd={{ initial: "2", md: "4" }}
        >
          {/* General info (Image, time, desc, tags, title) */}
          <Box>
            <SingleChallengeGeneralInfo
              data={data}
              isEdit={false}
              isPreview={isPreview}
              isPrivateForUser={isPrivateForUser}
            />
          </Box>
          {/* Buttons (Edit, Fav, Invite) */}
          {/* <Box>
            {!isPreview && !isPrivateForUser && (
              <SingleChallengeActionButtons
                data={data}
                setIsEdit={setIsEdit}
                isPreview={isPreview}
              />
            )}
          </Box> */}

          {/* Formula */}
          {!isPrivateForUser && (
            <>
              <SingleChallengeFormula
                coefficients={
                  data.challengeAdditionalData.fieldToCompareCoefficients
                }
                isRacing={isRacing}
                game={ChallengeGame}
                fieldToCompare={data.challengeAdditionalData.fieldToCompare}
              />

              {/* Additional info fields */}
              <Flex gap="3" wrap="wrap">
                {!isPrivateForUser &&
                  additionalFields
                    .filter((obj) => obj.value !== "")
                    .map((additionalField) => {
                      if (additionalField.value) {
                        return additionalField.title !== "Team size" &&
                          data.challengeData.game !== "PUBG" ? (
                          <SingleChallengeAdditionalInfoWithTooltip
                            title={additionalField.title}
                            value={additionalField.value}
                            gameType={ChallengeGame}
                            key={additionalField.title}
                            tooltip={additionalField.toolTipKey || null}
                          />
                        ) : (
                          data.challengeData.game === "PUBG" && (
                            <SingleChallengeAdditionalInfoWithTooltip
                              title={additionalField.title}
                              value={additionalField.value}
                              gameType={ChallengeGame}
                              key={additionalField.title}
                              tooltip={additionalField.toolTipKey || null}
                            />
                          )
                        );
                      }
                    })}
              </Flex>
              <Box py="5">
                <Separator decorative style={{ width: "100%" }} />
              </Box>
            </>
          )}

          {/* Author card */}
          {!isEventSite && ChallengeCreator && (
            <Skeleton loading={loadingZone}>
              <CreatorCard
                creatorProfile={ChallengeCreator}
                isFollowedByCurrentUser={data.isCreatorFollowedByCurrentUser}
                creatorFollowersNumber={data.creatorFollowersCount}
                zone={zone || zoneItem}
              />
            </Skeleton>
          )}

          {isEventSite && (
            <>
              <ChallengeTerms client={currentClient} />
            </>
          )}
        </Box>
      </Grid>
      <ChallengeDisclaimer game={ChallengeGame} />
    </Box>
  );
};
